import * as React from 'react';
import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';

import SharePointService from '../services/sharepoint.service';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`userdocspanel-${index}`} aria-labelledby={`userdocs-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}
CustomTabPanel.propTypes = { children: PropTypes.node, index: PropTypes.number.isRequired, value: PropTypes.number.isRequired };

function a11yProps(index, processstate) {
  return { id: `userdocs-${index}`, 'aria-controls': `userdocspanel-${index}`, 'data-process': processstate };
}

export default function UserDocuments({ handleActiveState }) {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const [value, setValue] = React.useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
    handleActiveState(parseInt(event.target.attributes['data-process'].nodeValue));
  };

  const [files, setFiles] = useState([]);
  const [working, setWorking] = useState(false);

  const GetSPFiles = async () => {
    setWorking(true);
    const allFiles = await SharePointService.getUserDocuments();
    if (allFiles.message !== 'Wrong Secret') {
      setFiles(allFiles);
      const keyname = Object.keys(allFiles)[0];
      handleActiveState(allFiles[keyname].categories.length);
    }
    setWorking(false);
  };

  const SPfetchFile = async (file) => {
    try {
      let SPfile = await SharePointService.getDownloadUserFile(file, file.gelesen);
      const blob = new Blob([SPfile], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const SPopenFile = async (file) => {
    try {
      let SPfile = await SharePointService.getDownloadUserFile(file, file.gelesen);
      const blob = new Blob([SPfile], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const didNotRead = (
    <span style={{ top: '1px', padding: '0px 10px', color: '#3A8787', fontWeight: 'bold', textShadow: '#3A8787 1px 0 16px' }}>
      Neu!
    </span>
  );

  useEffect(() => {
    GetSPFiles().catch(console.error);
  }, []);

  let content = (
    <Typography variant="h6" mt={4}>
      Keine Dateien zur Anzeige gefunden, bitte melden Sie sich bei Ihrer Kundenmanagerin.
    </Typography>
  );

  if (files.length !== 0) {
    content = (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile onChange={handleChange}>
            {Object.entries(files).map((flat, index) => (
              <Tab key={flat[0]} label={flat[0]} {...a11yProps(index + 10, flat[1].categories.length)} />
            ))}
          </Tabs>
        </Box>

        {Object.keys(files).length === undefined
          ? ''
          : Object.entries(files).map((flat, index) => (
              <CustomTabPanel key={flat[0]} value={value} index={index}>
                <Box>
                  {Object.entries(flat[1].categories.sort()).map((category) => (
                    <Box key={category[0]} sx={{ pt: 3 }}>
                      <h5 align="left" style={{ marginBottom: 0 }}>{category[1] ? category[1].substring(3) : ''}</h5>

                      <TableContainer sx={{ mt: 1 }}>
                        <Table size="small" sx={{ minWidth: 150 }}>
                          {/* Desktop-Header */}
                          {!isMdDown && (
                            <TableHead>
                              <TableRow>
                                <TableCell align="left" sx={{ fontWeight: 'bold', '@media screen and (min-width: 960px)': { width: '50%' } }}>
                                  Dateiname
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  Gelesen/Geändert
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', width: 100 }}>
                                  Download
                                </TableCell>
                              </TableRow>
                            </TableHead>
                          )}

                          <TableBody>
                            {Object.entries(flat[1].files)
                              .sort((a, b) => (a[1].name > b[1].name ? 1 : -1))
                              .map((file) =>
                                file[1].category === category[1] ? (
                                  !isMdDown ? (
                                    // Desktop-Zeile (3 Spalten)
                                    <TableRow key={file[0]}>
                                      <TableCell
                                        sx={{ '&:hover': { cursor: 'pointer' }, '@media screen and (min-width: 960px)': { width: '50%' } }}
                                        onClick={() => SPopenFile(file[1])}
                                      >
                                        {file[1].gelesen ? '' : didNotRead}
                                        {file[1].name}
                                      </TableCell>
                                      <TableCell align="right">{file[1].edited}</TableCell>
                                      <TableCell align="right" sx={{ width: 100 }}>
                                        <IconButton
                                          aria-label="download"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            SPfetchFile(file[1]);
                                          }}
                                        >
                                          <DownloadIcon />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    // Mobile/Tablet-Zeile (kompakt)
                                    <TableRow key={file[0]}>
                                      <TableCell colSpan={3} sx={{ py: 1.25 }}>
                                        <Box
                                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, cursor: 'pointer' }}
                                          onClick={() => SPopenFile(file[1])}
                                        >
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {file[1].gelesen ? '' : didNotRead}
                                              {file[1].name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {file[1].edited}
                                            </Typography>
                                          </Box>

                                          <IconButton
                                            aria-label="download"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              SPfetchFile(file[1]);
                                            }}
                                          >
                                            <DownloadIcon />
                                          </IconButton>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )
                                ) : null
                              )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </Box>
              </CustomTabPanel>
            ))}
      </>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {working ? (
        <Box py={10}>
          <CircularProgress />
        </Box>
      ) : (
        content
      )}
    </Box>
  );
}
